import { Capacitor, registerPlugin } from '@capacitor/core';

const DigiQinFile = registerPlugin('DigiQinFile');

/**
 * Phone, Android tablet, iPhone, and iPad — including the native app.
 * Desktop browsers stay on the normal download and print path.
 */
export function isHandheldDevice() {
  if (typeof navigator === 'undefined') return false;
  try {
    if (Capacitor.isNativePlatform()) return true;
  } catch {
    /* not running inside Capacitor */
  }
  const ua = navigator.userAgent || '';
  const iPadOs = navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;
  if (/iPhone|iPod|iPad/i.test(ua) || iPadOs) return true;
  if (/Android/i.test(ua)) return true;
  return false;
}

function isNativeApp() {
  try {
    return Capacitor.isNativePlatform();
  } catch {
    return false;
  }
}

function isNativeIos() {
  try {
    return Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'ios';
  } catch {
    return false;
  }
}

async function nativeFileCall(method, payload) {
  if (!isNativeApp()) return false;
  try {
    await DigiQinFile[method](payload);
    return true;
  } catch (error) {
    const bridge = window.DigiQinFile;
    if (method === 'saveFile' && typeof bridge?.saveFile === 'function') {
      const saved = bridge.saveFile(payload.filename, payload.mime, payload.base64);
      if (saved) return true;
    }
    if (method === 'printPdf' && typeof bridge?.printPdf === 'function') {
      if (bridge.printPdf(payload.filename, payload.base64)) return true;
    }
    if (method === 'printHtml' && typeof bridge?.printHtml === 'function') {
      if (bridge.printHtml(payload.title, payload.html)) return true;
    }
    const message = error?.message || '';
    if (/not implemented/i.test(message)) {
      throw new Error('Rebuild and reinstall the app, then try again');
    }
    throw error instanceof Error ? error : new Error('Could not save the file on this device');
  }
}

export function safeFilename(name, fallback = 'download') {
  const cleaned = String(name || '')
    .replace(/[\\/:*?"<>|]/g, '_')
    .replace(/\s+/g, ' ')
    .trim();
  if (!cleaned || cleaned === '.' || cleaned === '..') return fallback;
  return cleaned;
}

function blobToBase64(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const text = String(reader.result || '');
      const comma = text.indexOf(',');
      resolve(comma >= 0 ? text.slice(comma + 1) : text);
    };
    reader.onerror = () => reject(reader.error || new Error('Could not read the file'));
    reader.readAsDataURL(blob);
  });
}

const iosPending = new Map();
let iosSeq = 0;

function installIosCallback() {
  if (typeof window === 'undefined' || window.__dqFileDone) return;
  window.__dqFileDone = (payload) => {
    const id = payload && payload.id;
    const pending = iosPending.get(id);
    if (!pending) return;
    iosPending.delete(id);
    if (payload.ok) pending.resolve(payload.message || '');
    else pending.reject(new Error(payload.message || 'Could not save the file on this device'));
  };
}

async function iosHandler() {
  if (!isNativeIos() || typeof window === 'undefined') return null;
  installIosCallback();
  for (let attempt = 0; attempt < 8; attempt += 1) {
    const handler = window.webkit?.messageHandlers?.digiqinFile;
    if (handler) return handler;
    // The native bridge is attached when the app becomes active.
    // eslint-disable-next-line no-await-in-loop
    await new Promise((resolve) => { window.setTimeout(resolve, 150); });
  }
  return null;
}

function callIos(action, payload) {
  return iosHandler().then((handler) => {
    if (!handler) throw new Error('Could not save the file on this device');
    const id = `f${Date.now()}_${iosSeq += 1}`;
    return new Promise((resolve, reject) => {
      iosPending.set(id, { resolve, reject });
      try {
        handler.postMessage({ action, id, ...payload });
      } catch (error) {
        iosPending.delete(id);
        reject(error);
      }
    });
  });
}

function anchorDownload(blob, filename, { revokeMs = 0, newTab = false } = {}) {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  if (newTab) link.target = '_blank';
  document.body.appendChild(link);
  link.click();
  link.remove();
  if (revokeMs > 0) window.setTimeout(() => window.URL.revokeObjectURL(url), revokeMs);
  else window.URL.revokeObjectURL(url);
}

async function shareFile(blob, filename) {
  const type = blob.type || 'application/octet-stream';
  const file = new File([blob], filename, { type });
  const canShare = typeof navigator.share === 'function'
    && typeof navigator.canShare === 'function'
    && navigator.canShare({ files: [file] });
  if (!canShare) return false;
  await navigator.share({ files: [file], title: filename });
  return true;
}

function savePrompt(blob, filename) {
  return new Promise((resolve) => {
    const wrap = document.createElement('div');
    wrap.setAttribute('role', 'dialog');
    wrap.style.cssText = [
      'position:fixed', 'inset:0', 'z-index:2147483647', 'background:rgba(0,0,0,.45)',
      'display:flex', 'align-items:flex-end', 'justify-content:center', 'padding:16px',
    ].join(';');
    const card = document.createElement('div');
    card.style.cssText = 'background:#fff;border-radius:16px;padding:16px;width:min(420px,100%);box-shadow:0 12px 40px rgba(0,0,0,.2)';
    const title = document.createElement('p');
    title.textContent = 'Save file';
    title.style.cssText = 'margin:0 0 6px;font:600 16px/1.3 sans-serif;color:#111';
    const detail = document.createElement('p');
    detail.textContent = filename;
    detail.style.cssText = 'margin:0 0 14px;font:400 13px/1.4 sans-serif;color:#444;word-break:break-all';
    const row = document.createElement('div');
    row.style.cssText = 'display:flex;gap:8px';
    const saveBtn = document.createElement('button');
    saveBtn.type = 'button';
    saveBtn.textContent = 'Save';
    saveBtn.style.cssText = 'flex:1;min-height:44px;border:0;border-radius:10px;background:#ea580c;color:#fff;font:600 15px sans-serif';
    const closeBtn = document.createElement('button');
    closeBtn.type = 'button';
    closeBtn.textContent = 'Close';
    closeBtn.style.cssText = 'min-height:44px;padding:0 16px;border:0;border-radius:10px;background:#f3f4f6;color:#111;font:600 15px sans-serif';
    const close = () => {
      wrap.remove();
      resolve();
    };
    closeBtn.onclick = close;
    saveBtn.onclick = async () => {
      try {
        const shared = await shareFile(blob, filename);
        if (!shared) anchorDownload(blob, filename, { revokeMs: 60_000, newTab: true });
      } catch (error) {
        if (error?.name !== 'AbortError') anchorDownload(blob, filename, { revokeMs: 60_000, newTab: true });
      }
      close();
    };
    row.append(saveBtn, closeBtn);
    card.append(title, detail, row);
    wrap.appendChild(card);
    document.body.appendChild(wrap);
  });
}

export async function saveFileOnHandheld(blob, filename) {
  const name = safeFilename(filename, 'download');
  const typed = blob instanceof Blob ? blob : new Blob([blob]);
  const mime = typed.type || 'application/octet-stream';

  if (isNativeApp()) {
    const base64 = await blobToBase64(typed);
    await nativeFileCall('saveFile', { filename: name, mime, base64 });
    return;
  }

  if (isNativeIos()) {
    try {
      const base64 = await blobToBase64(typed);
      await callIos('save', { filename: name, mime, base64 });
      return;
    } catch (error) {
      if (!window.webkit?.messageHandlers?.digiqinFile) {
        /* fall through to the browser save sheet */
      } else {
        throw error;
      }
    }
  }

  try {
    const shared = await shareFile(typed, name);
    if (shared) return;
  } catch (error) {
    if (error?.name === 'AbortError') return;
    if (error?.name !== 'NotAllowedError') {
      /* try the tap-to-save sheet below */
    }
  }

  const iosBrowser = /iPhone|iPod|iPad/i.test(navigator.userAgent || '')
    || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  if (iosBrowser) {
    await savePrompt(typed, name);
    return;
  }

  anchorDownload(typed, name, { revokeMs: 60_000, newTab: false });
}

const PRINT_STYLE_ID = 'dq-handheld-print-style';
const PRINT_ROOT_ID = 'dq-handheld-print-root';

function removePrintUi() {
  document.getElementById(PRINT_ROOT_ID)?.remove();
  document.getElementById(PRINT_STYLE_ID)?.remove();
}

function ensurePrintStyle() {
  if (document.getElementById(PRINT_STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = PRINT_STYLE_ID;
  style.textContent = `
    @media print {
      body * { visibility: hidden !important; }
      #${PRINT_ROOT_ID}, #${PRINT_ROOT_ID} * { visibility: visible !important; }
      #${PRINT_ROOT_ID} {
        position: absolute !important;
        left: 0 !important;
        top: 0 !important;
        width: 100% !important;
        height: auto !important;
        background: #fff !important;
        overflow: visible !important;
      }
      #${PRINT_ROOT_ID} [data-print-chrome] { display: none !important; }
    }
  `;
  document.head.appendChild(style);
}

function whenImagesReady(root) {
  const images = Array.from(root.querySelectorAll('img'));
  if (!images.length) return new Promise((resolve) => { window.setTimeout(resolve, 200); });
  return new Promise((resolve) => {
    let pending = images.length;
    let settled = false;
    const finish = () => {
      if (settled) return;
      settled = true;
      resolve();
    };
    const mark = () => {
      pending -= 1;
      if (pending <= 0) finish();
    };
    images.forEach((img) => {
      if (img.complete) mark();
      else {
        img.addEventListener('load', mark, { once: true });
        img.addEventListener('error', mark, { once: true });
      }
    });
    window.setTimeout(finish, 1500);
  });
}

function printChrome(onPrint, onSave, onClose) {
  const bar = document.createElement('div');
  bar.setAttribute('data-print-chrome', '1');
  bar.style.cssText = 'display:flex;gap:8px;padding:12px;background:#111;position:sticky;top:0;z-index:2';
  const make = (label, background) => {
    const button = document.createElement('button');
    button.type = 'button';
    button.textContent = label;
    button.style.cssText = `flex:1;min-height:44px;border:0;border-radius:10px;background:${background};color:#fff;font:600 15px sans-serif`;
    return button;
  };
  const printBtn = make('Print', '#ea580c');
  const closeBtn = make('Close', '#4b5563');
  printBtn.onclick = onPrint;
  closeBtn.onclick = onClose;
  bar.append(printBtn);
  if (onSave) {
    const saveBtn = make('Save', '#1d4ed8');
    saveBtn.onclick = onSave;
    bar.append(saveBtn);
  }
  bar.append(closeBtn);
  return bar;
}

function browserPrint() {
  try {
    window.focus();
    window.print();
    return true;
  } catch {
    return false;
  }
}

export async function printHtmlOnHandheld(html, title) {
  const job = safeFilename(title, 'Print');
  if (isNativeApp()) {
    await nativeFileCall('printHtml', { title: job, html: String(html || '') });
    return true;
  }
  if (isNativeIos()) {
    try {
      const handler = await iosHandler();
      if (handler) {
        await callIos('printHtml', { title: job, html: String(html || '') });
        return true;
      }
    } catch {
      /* show the on-page print controls */
    }
  }

  removePrintUi();
  ensurePrintStyle();
  const layer = document.createElement('div');
  layer.id = PRINT_ROOT_ID;
  layer.style.cssText = 'position:fixed;inset:0;z-index:2147483646;background:#fff;overflow:auto;-webkit-overflow-scrolling:touch';
  const parsed = new DOMParser().parseFromString(String(html || ''), 'text/html');
  parsed.querySelectorAll('script').forEach((node) => node.remove());
  const styles = document.createElement('div');
  parsed.querySelectorAll('style').forEach((node) => styles.appendChild(document.importNode(node, true)));
  const content = document.createElement('div');
  content.innerHTML = parsed.body ? parsed.body.innerHTML : String(html || '');
  content.querySelectorAll('script').forEach((node) => node.remove());
  layer.append(
    printChrome(
      () => { browserPrint(); },
      null,
      () => removePrintUi(),
    ),
    styles,
    content,
  );
  document.body.appendChild(layer);
  await whenImagesReady(layer);
  browserPrint();
  return true;
}

export async function printPdfOnHandheld(blob, filename) {
  const name = safeFilename(filename, 'document.pdf');
  const pdfBlob = blob instanceof Blob && blob.type && blob.type.includes('pdf')
    ? blob
    : new Blob([blob], { type: 'application/pdf' });

  if (isNativeApp()) {
    const base64 = await blobToBase64(pdfBlob);
    await nativeFileCall('printPdf', { filename: name, base64 });
    return true;
  }

  if (isNativeIos()) {
    try {
      const handler = await iosHandler();
      if (handler) {
        const base64 = await blobToBase64(pdfBlob);
        await callIos('printPdf', { filename: name, base64 });
        return true;
      }
    } catch {
      /* show the on-page print controls */
    }
  }

  const url = window.URL.createObjectURL(pdfBlob);
  removePrintUi();
  ensurePrintStyle();
  const layer = document.createElement('div');
  layer.id = PRINT_ROOT_ID;
  layer.style.cssText = 'position:fixed;inset:0;z-index:2147483646;background:#fff;display:flex;flex-direction:column';
  const frame = document.createElement('iframe');
  frame.title = name;
  frame.src = url;
  frame.style.cssText = 'flex:1;width:100%;border:0;background:#fff';
  layer.append(
    printChrome(
      () => { browserPrint(); },
      () => { void saveFileOnHandheld(pdfBlob, name); },
      () => {
        removePrintUi();
        window.setTimeout(() => window.URL.revokeObjectURL(url), 1000);
      },
    ),
    frame,
  );
  document.body.appendChild(layer);
  const iosBrowser = /iPhone|iPod|iPad/i.test(navigator.userAgent || '')
    || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  if (!iosBrowser) window.setTimeout(() => browserPrint(), 400);
  return true;
}
